import { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { AuthUser } from '../types';
import { toast } from '../utils/toast';
import { authEventTracker } from '../utils/authEventTracker';

interface AuthState {
  user: AuthUser | null;
  session: any | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
}

class AuthStore {
  private state: AuthState = {
    user: null,
    session: null,
    loading: true,
    initialized: false,
    error: null
  };

  private listeners: Array<(state: AuthState) => void> = [];
  private hasShownWelcomeToast = false;
  private hasShownVerificationToast = false;
  private isProcessingAuth = false;
  private authListener: any = null;

  getState(): AuthState {
    return this.state;
  }

  subscribe(listener: (state: AuthState) => void) {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private setState(newState: Partial<AuthState>) {
    this.state = { ...this.state, ...newState };
    this.listeners.forEach(listener => listener(this.state));
  }

  // Public method for external updates
  updateState(newState: Partial<AuthState>) {
    this.setState(newState);
  }

  async signUp(email: string, password: string, fullName?: string): Promise<boolean> {
    // Track signup attempt
    authEventTracker.track('user-signup-attempt', { email });
    
    // Prevent multiple simultaneous signup attempts
    if (this.state.loading || this.isProcessingAuth) return false;
    
    this.isProcessingAuth = true;
    this.setState({ loading: true, error: null });
    
    try {
      const response = await authService.signUp({ email, password, fullName });
      
      if (response?.user) {
        // Track signup success
        authEventTracker.track('user-signup-success', { email });
        
        // Check if email is confirmed
        if (response.user.user_metadata && !response.session) {
          // User created but email not confirmed
          this.setState({ loading: false });
          toast.success('Account Created', 'Please check your email to verify.');
          return true;
        } else if (response.session) {
          // User created and email confirmed - update state immediately
          this.setState({
            user: response.user,
            session: response.session,
            loading: false,
          });
          
          // Run background tasks without blocking UI
          setTimeout(() => {
            authService.createUserSettings(response.user.id).catch(console.warn);
            window.dispatchEvent(new CustomEvent('creditsUpdated'));
          }, 0);
          
          if (!this.hasShownWelcomeToast) {
            this.hasShownWelcomeToast = true;
            toast.success('Welcome!');
          }
          return true;
        }
      }
      
      return false;
    } catch (error: any) {
      this.setState({ loading: false, error: error.message });
      authEventTracker.track('user-signup-failed', { email, error: error.message });
      throw error;
    } finally {
      this.isProcessingAuth = false;
    }
  }

  async signIn(email: string, password: string): Promise<boolean> {
    // Track login attempt
    authEventTracker.track('user-login-attempt', { email });
    
    // Prevent multiple simultaneous signin attempts
    if (this.state.loading || this.isProcessingAuth) return false;
    
    this.isProcessingAuth = true;
    this.setState({ loading: true, error: null });
    
    try {
      const response = await authService.signIn({ email, password });
      
      if (response.user) {
        // Track login success
        authEventTracker.track('user-login-success', { email });
        
        // Update state immediately for fast UI response
        this.setState({
          user: response.user,
          session: response.session,
          loading: false,
        });
        
        // Run background tasks without blocking UI
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('creditsUpdated'));
        }, 0);
        
        return true;
      } else {
        // Track login failure
        authEventTracker.track('user-login-failed', { email, error: 'Invalid credentials' });
        
        this.setState({ loading: false });
        return false;
      }
    } catch (error: any) {
      // Track login failure with error
      authEventTracker.track('user-login-failed', { email, error: error.message });
      
      this.setState({ loading: false, error: error.message });
      return false;
    } finally {
      this.isProcessingAuth = false;
    }
  }

  private isSigningOut = false;

  async signOut(): Promise<void> {
    // Track logout
    authEventTracker.track('user-logout', { email: this.state.user?.email });
    
    // Prevent multiple simultaneous logout attempts
    if (this.state.loading || this.isSigningOut) return;
    
    this.isSigningOut = true;
    
    try {
      // Clear state immediately for instant UI response
      this.setState({
        user: null,
        session: null,
        loading: false,
        error: null,
      });
      
      // Reset toast flags immediately
      this.hasShownWelcomeToast = false;
      this.hasShownVerificationToast = false;
      
      // Perform cleanup in background without blocking UI
      Promise.all([
        authService.signOut(),
        import('./dataCacheStore').then(({ dataCacheManager }) => dataCacheManager.clearCache())
      ]).catch(console.warn);
      
    } catch (error: any) {
      console.warn('Sign out error:', error);
      // Ensure state is cleared even if cleanup fails
      this.setState({
        user: null,
        session: null,
        loading: false,
        error: null,
      });
      this.hasShownWelcomeToast = false;
      this.hasShownVerificationToast = false;
    } finally {
      this.isSigningOut = false;
    }
  }

  async resetPassword(email: string): Promise<boolean> {
    // Track password reset request
    authEventTracker.track('password-reset-requested', { email });
    
    this.setState({ loading: true, error: null });
    
    try {
      await authService.resetPassword(email);
      
      this.setState({ loading: false });
      return true;
    } catch (error: any) {
      this.setState({ loading: false, error: error.message });
      return false;
    }
  }

  async updatePassword(newPassword: string): Promise<boolean> {
    this.setState({ loading: true, error: null });
    
    try {
      await authService.updatePassword(newPassword);
      
      this.setState({ loading: false });
      return true;
    } catch (error: any) {
      this.setState({ loading: false, error: error.message });
      return false;
    }
  }

  async updateProfile(metadata: { full_name?: string; phone?: string; username?: string }): Promise<boolean> {
    this.setState({ loading: true, error: null });
    
    try {
      const { profileService } = await import('../services/profileService');
      
      // Update user_settings table with all data
      await profileService.updateUserProfile(this.state.user!.id, {
        fullName: metadata.full_name,
        phone: metadata.phone,
        username: metadata.username
      });
      
      // Update auth metadata if full_name is provided
      if (metadata.full_name) {
        const updatedUser = await authService.updateUserMetadata({ full_name: metadata.full_name });
        this.setState({ user: updatedUser });
      }
      
      this.setState({ loading: false });
      return true;
    } catch (error: any) {
      this.setState({ loading: false, error: error.message });
      return false;
    }
  }

  async initialize(): Promise<void> {
    try {
      const response = await authService.getCurrentSession();

      if (response.user) {
        // Fetch profile settings, admin status, and block status
        try {
          const { supabase } = await import('../config/supabase');
          
          const [profileRes, adminRes, accountRes] = await Promise.all([
            supabase.from('user_settings').select('profile_picture_url').eq('user_id', response.user.id).maybeSingle(),
            supabase.from('admin_users').select('id').eq('id', response.user.id).maybeSingle(),
            supabase.from('user_accounts').select('is_blocked').eq('user_id', response.user.id).maybeSingle()
          ]);

          if (accountRes.data?.is_blocked) {
            toast.error('Account Suspended', 'Your account has been suspended by an administrator.');
            this.setState({
              user: null,
              session: null,
              loading: false,
              initialized: true,
            });
            supabase.auth.signOut().catch(console.warn);
            return;
          }

          this.setState({
            user: {
              ...response.user,
              profile_picture_url: profileRes.data?.profile_picture_url,
              isAdmin: !!adminRes.data,
              is_blocked: false,
            },
            session: response.session,
            loading: false,
            initialized: true,
          });
        } catch (e) {
          console.warn('Failed to fetch profile/admin details on init:', e);
          this.setState({
            user: response.user,
            session: response.session,
            loading: false,
            initialized: true,
          });
        }
        
        // Transfer any anonymous credits on session restore (handled by check-credits Edge Function)
        window.dispatchEvent(new CustomEvent('creditsUpdated'));
      } else {
        this.setState({
          user: null,
          session: null,
          loading: false,
          initialized: true,
        });
      }

      // Set up auth state change listener only once
      if (!this.authListener) {
        const { supabase } = await import('../config/supabase');
        this.authListener = supabase.auth.onAuthStateChange(async (event, session) => {
          console.log('🔐 Auth Event:', event, session?.user?.email);
          
          switch (event) {
            case 'INITIAL_SESSION':
            case 'SIGNED_IN':
              await this.handleSignedIn(session);
              break;
            case 'SIGNED_OUT':
              await this.handleSignedOut();
              break;
            case 'TOKEN_REFRESHED':
              await this.handleTokenRefreshed(session);
              break;
            case 'USER_UPDATED':
              await this.handleUserUpdated(session);
              break;
            case 'PASSWORD_RECOVERY':
              this.handlePasswordRecovery();
              break;
          }
        });
      }
      
    } catch (error: any) {
      console.error('Auth initialization error:', error);
      
      this.setState({
        user: null,
        session: null,
        loading: false,
        initialized: true,
        error: error.message,
      });
    }
  }

  private async handleSignedIn(session: any): Promise<void> {
    if (!session?.user) return;
    
    // Only proceed if email is confirmed
    if (session.user.email_confirmed_at) {
      const isExistingUser = this.state.user?.id === session.user.id;
      
      // Get user profile, admin status, and block status
      let userName = session.user.email?.split('@')[0] || '';
      let profile: any = null;
      let isAdmin = false;
      let isBlocked = false;

      try {
        const { supabase } = await import('../config/supabase');
        
        const [profileRes, adminRes, accountRes] = await Promise.all([
          supabase.from('user_settings').select('*').eq('user_id', session.user.id).maybeSingle(),
          supabase.from('admin_users').select('id').eq('id', session.user.id).maybeSingle(),
          supabase.from('user_accounts').select('is_blocked').eq('user_id', session.user.id).maybeSingle()
        ]);
        
        profile = profileRes.data;
        isAdmin = !!adminRes.data;
        isBlocked = !!accountRes.data?.is_blocked;

        if (isBlocked) {
          toast.error('Account Suspended', 'Your account has been suspended by an administrator.');
          this.setState({
            user: null,
            session: null,
            loading: false,
          });
          supabase.auth.signOut().catch(console.warn);
          return;
        }

        if (profile) {
          userName = session.user.user_metadata?.full_name || userName;
        } else {
          await authService.createUserSettings(session.user.id).catch(console.warn);
        }
      } catch (error) {
        console.warn('Failed to fetch/create user profile/admin status:', error);
        await authService.createUserSettings(session.user.id).catch(console.warn);
      }

      this.setState({
        user: {
          id: session.user.id,
          email: session.user.email!,
          name: userName,
          profile_picture_url: profile?.profile_picture_url,
          user_metadata: session.user.user_metadata,
          isAdmin,
          is_blocked: isBlocked,
        },
        session,
        loading: false,
      });
      
      // Transfer anonymous credits on sign-in (handled by check-credits Edge Function)
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
      
      // Show verification toast
      if (!this.hasShownWelcomeToast && !this.hasShownVerificationToast && !isExistingUser && !this.isProcessingAuth) {
        this.hasShownVerificationToast = true;
        toast.success('Email Verified!', 'Welcome to FinCalc. You now have access to 2 more credits!');
      }
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('userSignedIn', { 
        detail: { user: session.user, isNewUser: !isExistingUser } 
      }));
    }
  }

  private async handleSignedOut(): Promise<void> {
    this.setState({
      user: null,
      session: null,
      loading: false,
    });
    this.hasShownWelcomeToast = false;
    this.hasShownVerificationToast = false;
    this.isProcessingAuth = false;
    
    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('userSignedOut'));
  }

  private async handleTokenRefreshed(session: any): Promise<void> {
    if (session?.user && this.state.user) {
      this.setState({ session });
      console.log('🔄 Token refreshed for user:', session.user.email);
    }
  }

  private async handleUserUpdated(session: any): Promise<void> {
    if (session?.user && this.state.user) {
      const updatedUser = {
        ...this.state.user,
        email: session.user.email!,
        user_metadata: session.user.user_metadata,
      };
      this.setState({ user: updatedUser, session });
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('userUpdated', { 
        detail: { user: updatedUser } 
      }));
    }
  }

  private handlePasswordRecovery(): void {
    toast.info('Password Reset', 'You can now set a new password');
  }

  clearError() {
    this.setState({ error: null });
  }

  // Cleanup method
  destroy() {
    if (this.authListener) {
      this.authListener.data.subscription.unsubscribe();
      this.authListener = null;
    }
    this.listeners = [];
  }

  get isAuthenticated(): boolean {
    return !!this.state.user;
  }
}

export const authStore = new AuthStore();

// React hook for using the auth store
export const useAuthStore = () => {
  const [state, setState] = useState(authStore.getState());

  useEffect(() => {
    const unsubscribe = authStore.subscribe(setState);
    
    // Listen for profile updates from cache
    const handleProfileUpdate = (event: CustomEvent) => {
      const profile = event.detail;
      const currentState = authStore.getState();
      if (currentState.user && profile) {
        // Update user data with profile information
        const updatedUser = {
          ...currentState.user,
          name: profile.full_name || currentState.user.name,
          phone: profile.phone,
          username: profile.username,
          profile_picture_url: profile.profile_picture_url
        };
        authStore.updateState({ user: updatedUser });
      }
    };
    
    window.addEventListener('profileUpdated', handleProfileUpdate as EventListener);
    
    // Only initialize if not already initialized to prevent multiple initializations
    if (!authStore.getState().initialized) {
      authStore.initialize();
    }

    return () => {
      unsubscribe();
      window.removeEventListener('profileUpdated', handleProfileUpdate as EventListener);
    };
  }, []); // Empty dependency array to run only once

  return {
    ...state,
    isAuthenticated: authStore.isAuthenticated,
    signUp: authStore.signUp.bind(authStore),
    signIn: authStore.signIn.bind(authStore),
    signOut: authStore.signOut.bind(authStore),
    resetPassword: authStore.resetPassword.bind(authStore),
    updatePassword: authStore.updatePassword.bind(authStore),
    updateProfile: authStore.updateProfile.bind(authStore),
    clearError: authStore.clearError.bind(authStore),
  };
};